import { useState } from 'react';
import BigNumber from 'bignumber.js';
import { Button, useNavigate, useQueryState, useInfiniteQuery, useI18n } from '@ywwwtseng/react-kit';
import * as web3 from '@ywwwtseng/web3';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { Users } from 'lucide-react';
import * as schema from '@/db/schema';
import { List } from '@/components/common/List';
import { CountdownTimer } from '@/components/common/CountdownTimer';
import { RedPackage2 } from '@/components/icons';
import * as utils from '@/utils';
import type { Gift } from '@/types';

export default function GiftHistoryScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'active' | 'ne:active'>('active');
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const { data: gifts } = useInfiniteQuery<Gift[]>('user_gifts', {
    params: {
      status,
      tg_chat_id: 'isNotNull',
      limit: 20,
    },
    refetchOnMount: true,
  });

  return (
    <div className="flex flex-col items-center justify-start w-full h-full gap-3 py-3 px-5">
      <div className="w-full flex items-center justify-start gap-2">
        <Button
          className="py-1 px-2"
          rounded="full"
          size="xs"
          color={status === 'active' ? 'primary' : 'secondary'}
          variant="contained"
          onClick={() => {
            setStatus('active');
          }}
        >
          {t('common.active')}
        </Button>
        <Button
          className="py-1 px-2"
          color={status === 'ne:active' ? 'primary' : 'secondary'}
          rounded="full"
          size="xs"
          variant="contained"
          onClick={() => {
            setStatus('ne:active');
          }}
        >
          {t('common.inactive')}
        </Button>
      </div>
      <List
        className="flex-1 flex flex-col items-start w-full pb-4 gap-2"
        data={gifts}
        noDataLabel="common.no_gifts_sent"
        fallback={
          <>
            <div className="w-full h-[60px] rounded-sm bg-white/5"></div>
            <div className="w-full h-[60px] rounded-sm bg-white/5"></div>
            <div className="w-full h-[60px] rounded-sm bg-white/5"></div>
          </>
        }
      >
        {(gift) => (
          <div
            key={gift.id}
            className="card flex items-start w-full gap-2"
            onClick={() => {
              navigate('Gift', { params: { gift_id: gift.id } });
            }}
          >
            <RedPackage2 width={20} height={20} className="min-w-5 max-w-5" />
            <div className="flex flex-col w-[calc(100%-28px)] items-start gap-1">
              <div className="w-full flex items-center gap-2">
                <Typography size="2" weight={600} ellipsis lineClamp={1}>
                  {gift.tg_chat_name}
                </Typography>
                <Typography size="2" weight={600} className="ml-auto">
                  {web3.utils.formatUnits(
                    gift.total,
                    Number(gift.token_id.split(':')[1])
                  )}
                </Typography>
                <Typography size="2" weight={600}>
                  {utils.token.getTokenSymbol(gift.token_id)}
                </Typography>
              </div>
              <div className="w-full flex flex-1 items-center gap-1">
                <Users size={14} />
                <Typography size="2" weight={600}>
                  {gift.recipients}
                </Typography>
                <Typography size="2" weight={600} i18n="common.received" />
                <Typography size="2" weight={600}>
                  {new BigNumber(
                    web3.utils.formatUnits(
                      new BigNumber(gift.total).minus(gift.balance).toFixed(),
                      Number(gift.token_id.split(':')[1])
                    )
                  )
                    .toFixed(6)
                    .replace(/\.?0+$/, '')}
                </Typography>
                <Typography size="2" weight={600}>
                  {utils.token.getTokenSymbol(gift.token_id)}
                </Typography>
                {gift.status === 'active' && gift.end_at && (
                  <div className="ml-auto">
                    <CountdownTimer gift={gift} />
                  </div>
                )}
                {gift.refunded && new BigNumber(gift.refunded).gt(0) && (
                  <div className="flex items-center gap-1 ml-auto">
                    <Typography size="2" weight={600} i18n="common.refunded" />
                    <Typography size="2" weight={600}>
                      {web3.utils.formatUnits(
                        gift.refunded,
                        Number(gift.token_id.split(':')[1])
                      )}
                    </Typography>
                    <Typography size="2" weight={600}>
                      {utils.token.getTokenSymbol(gift.token_id)}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </List>
    </div>
  );
}
